'use client';

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { createContext, useContextSelector } from 'use-context-selector';

import {
  CollectionProduct,
  ContentfulCollection,
} from '@/modules/product/models/collection.interface';

import { handleCatchError } from '@/utils/utils';

import { useFilteredCollectionProducts } from '../hooks/useFilteredCollectionProducts';
import { useSkuSearch } from '../hooks/useSkuSearch';
import { ProductPdpInterface } from '../models/product-pdp.interface';

type CollectionRoomContextType = {
  collection: ContentfulCollection;
  onSelectExactMatchProduct: (product: CollectionProduct) => void;
  productCollection:
    | (CollectionProduct & {
        fullProduct?: ProductPdpInterface;
      })
    | null;
  exactMatchProducts: CollectionProduct[];
  relatedProducts: ProductPdpInterface[];
  isLoadingProducts: boolean;
};

const CollectionRoomContext = createContext<CollectionRoomContextType | undefined>(undefined);

type CollectionProviderProps = {
  children: ReactNode;
  collection: ContentfulCollection;
};

export const CollectionProvider = ({ children, collection }: CollectionProviderProps) => {
  const filteredProducts = useFilteredCollectionProducts(collection?.products);

  const exactMatchProducts = useMemo(
    () => filteredProducts?.filter((product: CollectionProduct) => product.exactMatch) || [],
    [filteredProducts]
  );

  const exactMatchProductsSkus = useMemo(
    () => exactMatchProducts.map((product) => product.sku),
    [exactMatchProducts]
  );

  const [productCollection, setProductCollection] = useState<CollectionProduct | undefined>();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Initialize productCollection with the first exact match product once available
  useEffect(() => {
    if (exactMatchProducts.length === 0) {
      setProductCollection(undefined);
      return;
    }

    // Check if current product is still valid
    const isCurrentProductValid = exactMatchProducts.some(
      (product) => product.sku === productCollection?.sku
    );

    // If current product is not valid or doesn't exist, set to first available
    if (!isCurrentProductValid) {
      setProductCollection(exactMatchProducts[0]);
    }
  }, [exactMatchProducts, productCollection?.sku]);

  const { validProducts: exactMatchProductsCompleted, isPending: isLoadingExactMatch } =
    useSkuSearch(exactMatchProductsSkus);

  const relatedProductsSkus = useMemo(() => {
    // Don't return any products if productCollection is not yet initialized
    if (!productCollection) {
      return [];
    }

    return (
      filteredProducts
        ?.filter((product: CollectionProduct) => {
          if (product.exactMatch) {
            return false;
          }
          if (!productCollection?.surfaces?.length) {
            return true;
          }

          return product.surfaces?.some((surface) => {
            return productCollection?.surfaces?.some(
              (collectionSurface) => collectionSurface.label === surface.label
            );
          });
        })
        .map((product) => product.sku) || []
    );
  }, [filteredProducts, productCollection]);

  const { validProducts: relatedProducts, isPending: isLoadingRelated } =
    useSkuSearch(relatedProductsSkus);

  // Mark as loaded once we have products
  useEffect(() => {
    if (!hasLoadedOnce && relatedProducts.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [hasLoadedOnce, relatedProducts.length]);

  const isLoadingProducts = !hasLoadedOnce || isLoadingExactMatch || isLoadingRelated;

  const handleSelectExactMatchProduct = useCallback((product: CollectionProduct) => {
    setProductCollection(product);
  }, []);

  const fullProduct = useMemo(() => {
    if (!productCollection?.sku) {
      return undefined;
    }
    return exactMatchProductsCompleted.find(
      (product: ProductPdpInterface) => product.sku === productCollection.sku
    );
  }, [productCollection?.sku, exactMatchProductsCompleted]);

  const enrichedProductCollection = useMemo(() => {
    if (!productCollection) {
      return null;
    }

    return {
      ...productCollection,
      fullProduct,
    };
  }, [productCollection, fullProduct]);

  const value = useMemo(() => {
    return {
      onSelectExactMatchProduct: handleSelectExactMatchProduct,
      productCollection: enrichedProductCollection,
      relatedProducts,
      exactMatchProducts,
      isLoadingProducts,
    };
  }, [
    handleSelectExactMatchProduct,
    enrichedProductCollection,
    relatedProducts,
    exactMatchProducts,
    isLoadingProducts,
  ]);

  return (
    <CollectionRoomContext.Provider
      value={{
        ...value,
        collection,
      }}
    >
      {children}
    </CollectionRoomContext.Provider>
  );
};

export function useCollectionRoomSelector<K extends keyof CollectionRoomContextType>(key: K) {
  return useContextSelector(CollectionRoomContext, (state) => {
    if (!state) {
      const errorMsg = 'CollectionProvider is missing';
      handleCatchError(errorMsg);
      throw new Error(errorMsg);
    }
    return state[key];
  });
}
